import { Component, HostBinding, EventEmitter, Output, ElementRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { FleetCardsFeatureFlagService } from '@app/services/feature-flags/fleet-cards';
import { FleetCardsDataService } from '@app/services/fleet-cards-data';
import { DocumentsService } from '@app/admin/fleet-card/card-application/documents.service';
import { LocalStorage } from '@app/services/local-storage';
import { Session } from '@app/services/session';
import { SidebarService } from '@services/sidebar.service';
import { PendoAnalytics } from '@app/services/pendo-analytics';

import {
  setNextDisplayDate,
  setUpChecks,
} from '@app/lib/fleet-card/card-application-nudge-experiment';
import { CurrencyPipe } from '@angular/common';

import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';

import {
  FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG,
  FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE,
  PLAID_URL,
 } from '@app/lib/fleet-card/card-application-nudge-experiment';

@Component({
  selector: 'fleetcard-application-nudge-banner',
  templateUrl: './fleetcard-application-nudge-banner.component.html',
  styleUrls: ['./fleetcard-application-nudge-banner.component.scss'],
  providers: [DocumentsService],
})

export class FleetcardApplicationNudgeBannerComponent implements OnInit {
  @Output() public onBannerClose = new EventEmitter();
  @Output() public onBannerOpen = new EventEmitter();
  public potentialSavings: number = 0;
  public documentsUploaded: boolean = false;
  public company: IFleetCardCompany;
  public ccmdStatus: string;
  public bankAccountLinked: boolean;
  public fleetCardLinkBankAccountClicked: boolean;
  public header: string;
  public body: string;
  public btnText: string;
  public messageType: string;
  public fleetCardsApplicationNudgeMessageType: typeof FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE
    = FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE;
  public plaidUrl: string = PLAID_URL;
  private el: HTMLElement;

  constructor(
    private session: Session,
    private localStorage: LocalStorage,
    private fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
    private fleetCardsDataService: FleetCardsDataService,
    private documentsService: DocumentsService,
    private router: Router,
    private sidebarService: SidebarService,
    private currencyPipe: CurrencyPipe,
    private pendo: PendoAnalytics,
    el: ElementRef,
  ) {
    this.el = el.nativeElement;
  }
  @HostBinding('class.hide') private shouldHideBanner: boolean = true;

  public ngOnInit() {
    if (!this.fleetCardsFeatureFlagService.isFleetCardsFleetBundleExperimentEnabled() &&
      this.fleetCardsFeatureFlagService.getFleetCardsApplicationNudgeExperimentValue() !== 'banner' &&
      this.session.isTrialCustomer()) {
      return;
    }

    setUpChecks(
      'banner',
      this.fleetCardsFeatureFlagService,
      this.localStorage,
      this.documentsService,
      this.fleetCardsDataService,
      (documentsUploaded: boolean) => this.documentsUploaded = documentsUploaded,
      (company: IFleetCardCompany) => this.company = company,
      (bankAccountLinked: boolean) => this.bankAccountLinked = bankAccountLinked,
      (messageType: string) => this.messageType = messageType,
      (potentialSavings: number) => this.potentialSavings = potentialSavings,
      () => this.showBanner(),
    );
  }

  public showBanner(): void {
    console.log(this.messageType);
    this.pendo.trackEvent('Experiment - Application Nudge - Exposure - Banner', {
      message_type: this.messageType,
    });
    const potentialSavings: string = this.currencyPipe.transform(this.potentialSavings, 'USD', 'symbol', '1.0-0');

    if (this.messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount) {
      this.header = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.linkBankAccount.header;
      this.body = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.linkBankAccount.body;
      this.btnText = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.linkBankAccount.btnText;
    } else if (this.messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsFinishApplication) {
      this.header = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsFinishApplication.header;
      this.body = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsFinishApplication.body(potentialSavings);
      this.btnText = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsFinishApplication.btnText;
    } else { // upsell
      this.header = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsApplyNow.header(potentialSavings);
      this.body = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsApplyNow.body;
      this.btnText = FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG.potentialSavingsApplyNow.btnText;
    }

    this.onBannerOpen.emit();
    this.shouldHideBanner = false;
  }

  public closeBanner(): void {
    this.pendo.trackEvent('Experiment - Application Nudge - Banner - Dismiss', {
      message_type: this.messageType,
    });
    this.commonBannerCloseActions();
    if (!this.router.url.includes('/fleet-cards/')) {
      this.localStorage.setFleetCardNotificationDot(true);
      this.sidebarService.setFleetCardNotificationDot();
    }
  }

  public handleCTAClick(): void {
    this.pendo.trackEvent('Experiment - Application Nudge - Banner - Click', {
      message_type: this.messageType,
    });
    this.router.navigate(['/admin/fleet-cards/card-application']);
    this.commonBannerCloseActions();
  }

  private commonBannerCloseActions(): void {
    this.onBannerClose.emit();
    this.shouldHideBanner = true;
    setNextDisplayDate(this.localStorage, this.messageType);
  }

  public handlePlaidLinkClick(): void {
    if (this.messageType !== FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount) return;
    this.pendo.trackEvent('Experiment - Application Nudge - Banner - Click', {
      message_type: this.messageType,
      plaid_link: true,
    });
    window.open(this.plaidUrl, '_blank');
  }
}
